import type { FC } from 'react';

import type {
  SiteComplianceEvaluation,
  SiteComplianceSite,
} from '@/domains/siteCompliance';
import {
  SITE_COMPLIANCE_NETWORK_TIER_LABELS,
  SITE_COMPLIANCE_SITE_TYPE_LABELS,
  getSiteCompliancePrimaryDivisionLabel,
  normalizeSiteComplianceDivision,
} from '@/domains/siteCompliance';

interface SiteDetailFactsProps {
  latestEvaluation: SiteComplianceEvaluation | null;
  site: SiteComplianceSite | null;
}

const MISSING_INVENTORY_FIELD_TITLE = 'Field not provided by Inventory';

const MissingInventoryField = () => (
  <span title={MISSING_INVENTORY_FIELD_TITLE}>—</span>
);

const SiteDetailFacts: FC<SiteDetailFactsProps> = ({
  latestEvaluation,
  site,
}) => {
  if (!site) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-x-3 gap-y-3 lg:grid lg:grid-cols-2">
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Site ID</p>
        <p className="text-sm">{site.siteId}</p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Primary Division</p>
        <p className="text-sm">
          {getSiteCompliancePrimaryDivisionLabel(site)}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">All Divisions</p>
        <p className="text-sm">
          {site.primaryDivisions.length
            ? site.primaryDivisions.map(normalizeSiteComplianceDivision).join(', ')
            : 'N/A'}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Business Unit</p>
        <p className="text-sm">
          {site.businessUnits.length ? site.businessUnits.join(', ') : 'N/A'}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Site Type</p>
        <p className="text-sm">{SITE_COMPLIANCE_SITE_TYPE_LABELS[site.siteType]}</p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Network Tier</p>
        <p className="text-sm">
          {site.networkTier
            ? SITE_COMPLIANCE_NETWORK_TIER_LABELS[site.networkTier]
            : site.isFlagship
              ? 'Flagship'
              : site.isEnhanced
                ? 'Enhanced'
                : site.isDatacenter
                  ? 'Datacenter'
                : 'N/A'}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Region</p>
        <p className="text-sm">{site.region || <MissingInventoryField />}</p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Current Score</p>
        <p className="text-sm">
          {latestEvaluation ? `${latestEvaluation.score}%` : 'N/A'}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs lg:text-sm text-gray-500">Latest Evaluation ID</p>
        <p className="text-sm">{latestEvaluation?.id ?? 'N/A'}</p>
      </div>
      <div className="flex flex-col gap-1 lg:col-span-2">
        <p className="text-xs lg:text-sm text-gray-500">Address</p>
        <p className="text-sm">
          {site.address ?? 'Physical address not available.'}
        </p>
      </div>
    </div>
  );
};

export default SiteDetailFacts;
